import React from 'react';
import { useSEO } from '@/hooks/useSEO';
import { motion } from 'framer-motion';
import { FileText } from 'lucide-react';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import { Card } from '@/components/ui/Card';

const entries = [
  { version: '1.2.0', date: '2025-02-14', items: ['New blog and contact pages', 'Careers page and open roles', 'Footer links updated'] },
  { version: '1.1.0', date: '2025-02-01', items: ['AI coach improvements', 'Learning path enhancements', 'Micro-learning content updates'] },
  { version: '1.0.0', date: '2024-12-01', items: ['Launch of Five AI career coach', 'Personalized learning paths', 'Daily nudges and tips'] },
];

export const ChangelogPage: React.FC = () => {
  useSEO({ title: 'Changelog - Product Updates & New Features', description: 'See what is new in Five AI. Latest product updates, feature releases, and improvements to the AI career coaching platform.', path: '/changelog' });

  return (
    <main id="main-content" className="min-h-screen bg-background" role="main" aria-label="Changelog">
      <Header />
      <div className="pt-16">
        <section className="relative overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,hsl(var(--primary)/0.18),transparent_50%)] dark:bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,hsl(var(--primary)/0.25),transparent_50%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_40%_at_80%_20%,hsl(263_70%_50%/0.08),transparent)]" />
          <div className="container relative mx-auto px-4 py-24 sm:py-32">
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="text-center max-w-4xl mx-auto">
              <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.2 }} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-8">
                <FileText className="w-4 h-4 text-primary" />
                <span className="text-sm font-medium text-primary">Product updates</span>
              </motion.div>
              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-[1.1] tracking-tight">
                <span className="text-foreground">What's</span>
                <br />
                <span className="bg-gradient-to-r from-primary via-violet-500 to-fuchsia-500 bg-clip-text text-transparent">new</span>
              </h1>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-4">Recent updates and improvements to Five AI.</p>
              <p className="text-sm text-muted-foreground/80">Changelog</p>
            </motion.div>
          </div>
        </section>
        <section className="py-4 sm:py-8">
          <div className="container mx-auto px-4 max-w-6xl">
            <div className="max-w-2xl space-y-6">
              {entries.map((entry, i) => (
                <motion.div key={entry.version} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06, duration: 0.4 }}>
                  <Card hover className="p-6 sm:p-8 rounded-2xl border-2 hover:border-primary/40 transition-all duration-300 bg-gradient-to-br from-primary/5 to-violet-500/5">
                    <div className="flex flex-wrap items-center gap-2 mb-3">
                      <span className="font-bold text-foreground">{entry.version}</span>
                      <span className="text-sm text-muted-foreground">{entry.date}</span>
                    </div>
                    <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1 leading-relaxed">
                      {entry.items.map((item, j) => (
                        <li key={j}>{item}</li>
                      ))}
                    </ul>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      </div>
      <Footer />
    </main>
  );
};
